import { z } from 'zod';
import { LOCALES, LocalizedText } from './i18n';
import { Lighting, SceneManifest } from './scene';

export const ZONE_KEYS = [
  'upper_cabinets',
  'lower_cabinets',
  'island',
  'countertop',
  'wall_cladding',
  'floor',
] as const;
export const ZoneKey = z.enum(ZONE_KEYS);
export type ZoneKey = z.infer<typeof ZoneKey>;

export const MATERIAL_TYPES = [
  'wood',
  'lacquer',
  'laminate',
  'stone',
  'ceramic',
  'concrete',
  'metal',
] as const;
export const MaterialType = z.enum(MATERIAL_TYPES);
export type MaterialType = z.infer<typeof MaterialType>;

export const SurfaceFinish = z.enum(['matte', 'satin', 'gloss']);
export type SurfaceFinish = z.infer<typeof SurfaceFinish>;

export const StockStatus = z.enum(['in_stock', 'low_stock', 'out_of_stock', 'unknown']);
export type StockStatus = z.infer<typeof StockStatus>;

export const Stock = z.object({
  status: StockStatus,
  available: z.number().nullable(),
  updatedAt: z.string().nullable(),
});
export type Stock = z.infer<typeof Stock>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * How a texture is applied. The texture image's vertical axis is the grain direction;
 * `sizeMm` is the real-world size covered by one tile of the texture.
 */
export const MaterialShading = z.object({
  /** 0 = satin reflection lobe, 1 = glossy lobe. */
  gloss: z.number().min(0).max(1),
  /** Reflection strength; 1 = plain dielectric (F0 = 0.04). */
  specular: z.number().min(0).max(8),
  /** 0 = dielectric (white reflections), 1 = metal (reflections tinted by the texture). */
  metallic: z.number().min(0).max(1),
  tint: hexColor,
  sizeMm: z.object({ w: z.number().positive(), h: z.number().positive() }),
  rotationDeg: z.number(),
});
export type MaterialShading = z.infer<typeof MaterialShading>;

export const TextureSet = z.object({
  /** URL per maximum texture dimension, e.g. `{ "1024": "...", "2048": "..." }`. */
  tiers: z.record(z.string(), z.string()),
  thumbnail: z.string(),
  averageColor: hexColor,
});
export type TextureSet = z.infer<typeof TextureSet>;

export const ZoneOverride = z.object({
  scale: z.number().positive().optional(),
  rotationDeg: z.number().optional(),
});
export type ZoneOverride = z.infer<typeof ZoneOverride>;

export const KitchenMaterial = z.object({
  id: z.string(),
  code: z.string(),
  name: LocalizedText,
  type: MaterialType,
  collection: z.string(),
  colorFamily: z.string(),
  finish: SurfaceFinish,
  shading: MaterialShading,
  textures: TextureSet,
  zones: z.array(ZoneKey),
  zoneOverrides: z.partialRecord(ZoneKey, ZoneOverride),
  erpItemCode: z.string().nullable(),
  stock: Stock,
  sort: z.number(),
});
export type KitchenMaterial = z.infer<typeof KitchenMaterial>;

export const KitchenZone = z.object({
  key: ZoneKey,
  name: LocalizedText,
  allowedTypes: z.array(MaterialType),
  sort: z.number(),
});
export type KitchenZone = z.infer<typeof KitchenZone>;

export const KitchenScene = z.object({
  id: z.string(),
  key: z.string(),
  name: LocalizedText,
  sort: z.number(),
  /** Base URL of the scene package; manifest file paths are relative to it. */
  baseUrl: z.string(),
  manifest: SceneManifest,
  defaults: z.partialRecord(ZoneKey, z.string()),
});
export type KitchenScene = z.infer<typeof KitchenScene>;

export const OutOfStockBehavior = z.enum(['hide', 'badge', 'disable']);
export type OutOfStockBehavior = z.infer<typeof OutOfStockBehavior>;

export const KitchenSettings = z.object({
  outOfStockBehavior: OutOfStockBehavior,
  lowStockThreshold: z.number().nonnegative(),
});
export type KitchenSettings = z.infer<typeof KitchenSettings>;

export const KitchenCatalog = z.object({
  version: z.number().int(),
  publishedAt: z.string(),
  zones: z.array(KitchenZone),
  scenes: z.array(KitchenScene),
  materials: z.array(KitchenMaterial),
  settings: KitchenSettings,
});
export type KitchenCatalog = z.infer<typeof KitchenCatalog>;

export const KitchenConfig = z.object({
  sceneKey: z.string().max(64),
  lighting: Lighting,
  /** zone → material id */
  selections: z.partialRecord(ZoneKey, z.string().max(64)),
});
export type KitchenConfig = z.infer<typeof KitchenConfig>;

export const KitchenProjectInfo = z.object({
  projectName: z.string().max(120).optional(),
  clientName: z.string().max(120).optional(),
  designerName: z.string().max(120).optional(),
  company: z.string().max(120).optional(),
  phone: z.string().max(40).optional(),
  email: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  /** Designer/studio logo for the PDF header: a PNG, JPEG or WebP data URL (≤ 300 kB). */
  logo: z
    .string()
    .max(400_000)
    .regex(
      /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/,
      'logo must be an image data URL',
    )
    .optional(),
});
export type KitchenProjectInfo = z.infer<typeof KitchenProjectInfo>;

export const KitchenExportRequest = z.object({
  config: KitchenConfig,
  sceneKeys: z.array(z.string()).min(1).max(4),
  lightings: z.array(Lighting).min(1).max(2),
  project: KitchenProjectInfo,
  locale: z.enum(LOCALES),
});
export type KitchenExportRequest = z.infer<typeof KitchenExportRequest>;

/** Default zone → allowed material types mapping used by the seed and admin UI. */
export const DEFAULT_ZONE_TYPES: Record<ZoneKey, MaterialType[]> = {
  upper_cabinets: ['wood', 'lacquer', 'laminate'],
  lower_cabinets: ['wood', 'lacquer', 'laminate', 'concrete', 'metal'],
  island: ['wood', 'lacquer', 'laminate', 'concrete', 'stone', 'metal'],
  countertop: ['stone', 'wood'],
  wall_cladding: ['ceramic', 'stone', 'concrete', 'wood', 'lacquer', 'metal'],
  floor: ['wood', 'ceramic', 'stone', 'concrete'],
};

/** Materials that may be offered for a zone, honouring stock behaviour. */
export function materialsForZone(
  catalog: Pick<KitchenCatalog, 'materials' | 'settings'>,
  zone: ZoneKey,
): KitchenMaterial[] {
  return catalog.materials
    .filter((m) => m.zones.includes(zone))
    .filter(
      (m) => !(catalog.settings.outOfStockBehavior === 'hide' && m.stock.status === 'out_of_stock'),
    )
    .sort((a, b) => a.sort - b.sort);
}

export function isSelectable(material: KitchenMaterial, settings: KitchenSettings): boolean {
  return !(settings.outOfStockBehavior !== 'badge' && material.stock.status === 'out_of_stock');
}

/**
 * Materials a visitor can actually choose for a zone, in catalog order: offered for the zone
 * (hidden out-of-stock items excluded), selectable, and of a type the zone allows.
 */
export function selectableMaterials(
  catalog: Pick<KitchenCatalog, 'materials' | 'settings'>,
  zone: Pick<KitchenZone, 'key' | 'allowedTypes'>,
): KitchenMaterial[] {
  return materialsForZone(catalog, zone.key).filter(
    (m) =>
      isSelectable(m, catalog.settings) &&
      (zone.allowedTypes.length === 0 || zone.allowedTypes.includes(m.type)),
  );
}

/**
 * Materials shown for one scene: the visitor's choice, else the scene default, else the first
 * material the zone offers. A choice or default that is no longer available (removed, hidden
 * because it is out of stock, or not allowed in the zone) is skipped, so the configurator, the
 * exported image and the specification always agree. A zone is left empty only when the catalog
 * offers nothing for it.
 */
export function effectiveSelections(
  catalog: Pick<KitchenCatalog, 'materials' | 'settings' | 'zones' | 'scenes'>,
  config: KitchenConfig,
): Partial<Record<ZoneKey, KitchenMaterial>> {
  const scene = catalog.scenes.find((s) => s.key === config.sceneKey);
  const out: Partial<Record<ZoneKey, KitchenMaterial>> = {};
  for (const zone of catalog.zones) {
    const available = selectableMaterials(catalog, zone);
    const find = (id: string | undefined) => (id ? available.find((m) => m.id === id) : undefined);
    const material =
      find(config.selections[zone.key]) ?? find(scene?.defaults[zone.key]) ?? available[0];
    if (material) out[zone.key] = material;
  }
  return out;
}
